import React, { type FC, Fragment, type ReactNode } from 'react'
import { Box, Divider, svgIconClasses, type SxProps, type Theme } from '@mui/material'

import { selectSideNavCollapsed, Translation } from '../../../domain'

import { IconButton, SidePanelCloseIcon } from '../common'
import { useTranslator } from '../Internationalization'
import { type AbsoluteRouteOnlyPath, useRouting } from '../routing'
import { useAppState, useAppUsecase } from '../State'
import { getSideBarIconSx, insetFocusOutline, SIDE_BAR_ICON_SIZE, THEME_SPACING_UNIT } from '../theme'
import { Brand } from './Brand'
import { getAllRouteOnlyPaths } from './Config'
import { useNavigationFormatter } from './Hooks'
import { NavigationPermissionedLink } from './Link'
import { useNavigationRouting } from './NavigationRoutingHook'
import { selectedNavigationItemStyle } from './Styles'

type Props = Readonly<{ route: AbsoluteRouteOnlyPath }>

const SIDEBAR_ANIMATION_CSS = '.25s ease-in-out'

const OPEN_WIDTH = 200

const ITEM_SIZE = 32
const ICON_SIZE = SIDE_BAR_ICON_SIZE

/** In spacing steps of THEME_SPACING_UNIT (4px), used both by the styles below and by the widths, so they can't drift apart */
const BODY_SIDE_PADDING = 2
const ITEM_SIDE_PADDING = 2
const ICON_TO_LABEL = 3
/**
 * The label's room on one line in the open side bar: 200px, less 8px side padding twice, 8px item padding twice, the icon and
 * the 12px gap. It is fixed, so while the side bar animates open the label already wraps as it will at full width, and the
 * narrow side bar clips it instead of squeezing it word by word.
 */
const LABEL_WIDTH = OPEN_WIDTH - (2 * BODY_SIDE_PADDING + 2 * ITEM_SIDE_PADDING + ICON_TO_LABEL) * THEME_SPACING_UNIT - ICON_SIZE

/** 48px: a 32px item plus the body's side padding, so a left-aligned item is already centred */
const CLOSED_WIDTH = ITEM_SIZE + 2 * BODY_SIDE_PADDING * THEME_SPACING_UNIT

/**
 * A label too long for one line wraps onto more lines rather than being cut off, so it is always readable in full, on touch
 * screens too. The item grows from 32px by 20px per extra line, and the icon stays level with the first line.
 */
const linkStyle: SxProps<Theme> = (theme) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: ICON_TO_LABEL,
    minHeight: ITEM_SIZE,
    boxSizing: 'border-box',
    px: ITEM_SIDE_PADDING,
    py: 1.5,
    borderRadius: 1,
    color: 'grey.700',
    typography: 'body2',
    overflow: 'hidden',
    '&:hover': { backgroundColor: 'grey.200' },
    '&:focus-visible': insetFocusOutline(theme),
})
/**
 * Closed, an item is a 32px square with its icon centred. The square itself stays left-aligned in the list: at 48px wide with
 * 8px padding it is already centred, and it doesn't jump to the middle while the width animates.
 */
const collapsedLinkStyle: SxProps<Theme> = { width: ITEM_SIZE, px: 0, justifyContent: 'center' }

/** The icon sits in a box as tall as one line of label text, so it stays level with the first line at any text size */
const iconBoxStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center', flexShrink: 0, height: (theme) => theme.typography.body2.lineHeight }
const iconStyle: SxProps<Theme> = { ...getSideBarIconSx(), color: 'grey.500' }
const selectedIconStyle: SxProps<Theme> = { color: 'primary.700' }

const labelStyle: SxProps<Theme> = { flexShrink: 0, width: LABEL_WIDTH, overflowWrap: 'break-word' }

const NavigationLink: FC<Props> = ({ route }) => {
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const collapsed = useAppState(selectSideNavCollapsed)

    const selected = routing.isAtRoute(route)
    const Icon = formatter.formatIcon(route)

    return (
        <NavigationPermissionedLink
            path={route}
            withTooltip={collapsed}
            aria-current={selected ? 'page' : undefined}
            sx={[linkStyle, selected && selectedNavigationItemStyle, collapsed && collapsedLinkStyle]}
        >
            <Box component="span" sx={iconBoxStyle}>
                <Icon sx={[iconStyle, selected && selectedIconStyle]} />
            </Box>
            {!collapsed && (
                <Box component="span" sx={labelStyle}>
                    {formatter.formatTitle(route)}
                </Box>
            )}
        </NavigationPermissionedLink>
    )
}

const listStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }

const NavigationLinkList: FC<Readonly<{ routes: readonly AbsoluteRouteOnlyPath[], 'data-testid'?: string }>> = ({ routes, ...props }) => (
    <Box sx={listStyle} {...props}>
        {routes.map((route) => <NavigationLink key={route} route={route} />)}
    </Box>
)

const drawerStyle: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    width: OPEN_WIDTH,
    backgroundColor: 'grey.100',
    transition: `width ${SIDEBAR_ANIMATION_CSS}`,
}
const collapsedDrawerStyle: SxProps<Theme> = { width: CLOSED_WIDTH }

const bodyStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, px: BODY_SIDE_PADDING, py: 4 }

const dividerStyle: SxProps<Theme> = { width: '100%', borderColor: 'grey.300' }
const collapsedDividerStyle: SxProps<Theme> = { width: ITEM_SIZE }

/**
 * The badge and the toggle sit at the bottom in the normal flow, so on a short window they never cover the items. They stay
 * right-aligned, so they slide with the edge while the width animates, and at 48px that is already centred.
 */
const footStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, mt: 'auto' }

/** The ghost button's own hover grey is the side bar's background, so it gets the side bar's item hover instead */
const toggleStyle: SxProps<Theme> = {
    width: ITEM_SIZE,
    height: ITEM_SIZE,
    color: 'grey.500',
    '&:hover': { backgroundColor: 'grey.200', color: 'grey.700' },
    '&:active': { backgroundColor: 'grey.300' },
    [`& .${svgIconClasses.root}`]: { width: ICON_SIZE, height: ICON_SIZE, transition: `transform ${SIDEBAR_ANIMATION_CSS}` },
}
const collapsedToggleIconStyle: SxProps<Theme> = { transform: 'scaleX(-1)' }

type SideNavigationProps = Readonly<{ deviationsBadge?: ReactNode }>

/**
 * `deviationsBadge` is an optional slot, not a direct import, since `Navigation/` and `DeviationNotifications/` are separate
 * feature folders. It sits in the bottom area, above the open/close toggle.
 */
export const SideNavigation: FC<SideNavigationProps> = ({ deviationsBadge }) => {
    const routing = useNavigationRouting()
    const groups = routing.getSideMenuGroups(getAllRouteOnlyPaths())

    const translator = useTranslator()
    const navigationUsecase = useAppUsecase('navigationUsecase')
    const collapsed = useAppState(selectSideNavCollapsed)

    return (
        <Box
            component="nav"
            aria-label={translator.formatTranslation(Translation.NAVIGATION_SIDE_LABEL)}
            sx={[drawerStyle, collapsed && collapsedDrawerStyle]}
            data-testid="side-navigation"
        >
            <Brand collapsed={collapsed} />
            <Box sx={bodyStyle}>
                {groups.map((group, k) => {
                    if (group.length === 0) {
                        return null
                    }

                    return (
                        <Fragment key={k}>
                            {k > 0 && <Divider data-testid="side-navigation-divider" sx={[dividerStyle, collapsed && collapsedDividerStyle]} />}
                            <NavigationLinkList data-testid={`side-navigation-${k + 1}`} routes={group} />
                        </Fragment>
                    )
                })}
                <Box sx={footStyle}>
                    {deviationsBadge}
                    <IconButton
                        data-testid="side-navigation-toggle"
                        variant="ghost"
                        aria-label={translator.formatTranslation(collapsed ? Translation.NAVIGATION_SIDE_EXPAND : Translation.NAVIGATION_SIDE_COLLAPSE)}
                        onClick={() => navigationUsecase.toggleSideNavCollapsed()}
                        sx={toggleStyle}
                    >
                        <SidePanelCloseIcon sx={[collapsed && collapsedToggleIconStyle]} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    )
}
